import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { 
    Search, Zap, Smartphone, Target, BarChart3, 
    CheckCircle, ArrowRight, FileText, Link2, 
    Image, Code, Layout, MousePointer
} from 'lucide-react';
import SEO, { SEO_CONFIG } from '../components/SEO';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

export default function FeaturesPage({ onRunAudit, onNavigate }) {
    const seo = SEO_CONFIG.features;
    const features = [
        {
            icon: Search,
            title: "SEO Analysis",
            color: "cyan",
            description: "Comprehensive search engine optimization review",
            items: [
                { icon: FileText, text: "Meta tags check" },
                { icon: FileText, text: "Missing titles/descriptions" },
                { icon: Layout, text: "Header structure" },
                { icon: Search, text: "Keyword usage" },
                { icon: BarChart3, text: "Basic on-page SEO health score" }
            ]
        },
        {
            icon: Zap,
            title: "Performance Insights",
            color: "yellow",
            description: "Speed and optimization analysis",
            items: [
                { icon: Zap, text: "Page load analysis" },
                { icon: CheckCircle, text: "Optimization opportunities" },
                { icon: Image, text: "Large image detection" },
                { icon: Code, text: "Script bloat identification" }
            ]
        },
        {
            icon: Smartphone,
            title: "Mobile Optimization",
            color: "green",
            description: "Mobile-first design evaluation",
            items: [
                { icon: Smartphone, text: "Mobile friendliness signals" },
                { icon: Layout, text: "Layout responsiveness feedback" },
                { icon: MousePointer, text: "CTA visibility" }
            ]
        },
        {
            icon: Target,
            title: "Conversion & UX Review",
            color: "magenta",
            description: "User experience and conversion optimization",
            items: [
                { icon: Target, text: "Call-to-action clarity" },
                { icon: CheckCircle, text: "Trust indicators" },
                { icon: Layout, text: "Visual hierarchy suggestions" },
                { icon: FileText, text: "Homepage messaging feedback" }
            ]
        }
    ];

    const colorClasses = {
        cyan: { bg: "from-cyan-500/20 to-blue-500/20", border: "border-cyan-500/20", text: "text-cyan-400", icon: "text-cyan-400" },
        yellow: { bg: "from-yellow-500/20 to-orange-500/20", border: "border-yellow-500/20", text: "text-yellow-400", icon: "text-yellow-400" },
        green: { bg: "from-green-500/20 to-emerald-500/20", border: "border-green-500/20", text: "text-green-400", icon: "text-green-400" },
        magenta: { bg: "from-pink-500/20 to-purple-500/20", border: "border-pink-500/20", text: "text-pink-400", icon: "text-pink-400" }
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
            
            {/* Navigation */}
            <Navbar activePage="features" onNavigate={onNavigate} onRunAudit={onRunAudit} />

            {/* Hero Section */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6">
                        Powerful AI Website Audits{' '}
                        <span className="bg-gradient-to-r from-cyan-400 to-magenta bg-clip-text text-transparent">
                            in Seconds
                        </span>
                    </h1>
                    <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
                        SiteGradePro analyzes your website's performance, SEO, usability, and optimization 
                        opportunities — all in one simple report.
                    </p>
                    <Button 
                        onClick={onRunAudit}
                        size="lg"
                        className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-10 py-6 text-lg"
                    >
                        Run a Free Audit
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>

            {/* Feature Sections */}
            <section className="py-16 px-4">
                <div className="max-w-6xl mx-auto space-y-16">
                    {features.map((feature, index) => {
                        const colors = colorClasses[feature.color];
                        const Icon = feature.icon;
                        const isEven = index % 2 === 0;
                        
                        return (
                            <div 
                                key={feature.title}
                                className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}
                            >
                                {/* Icon/Visual Side */}
                                <div className="flex-1 flex justify-center">
                                    <div className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${colors.bg} ${colors.border} border flex items-center justify-center`}>
                                        <Icon className={`h-16 w-16 ${colors.icon}`} />
                                    </div>
                                </div>
                                
                                {/* Content Side */}
                                <div className="flex-1">
                                    <h2 className={`text-3xl font-bold mb-3 ${colors.text}`}>
                                        {feature.title}
                                    </h2>
                                    <p className="text-gray-400 mb-6">{feature.description}</p>
                                    <ul className="space-y-3">
                                        {feature.items.map((item, i) => {
                                            const ItemIcon = item.icon;
                                            return (
                                                <li key={i} className="flex items-center gap-3 text-gray-300">
                                                    <CheckCircle className={`h-5 w-5 ${colors.icon} shrink-0`} />
                                                    {item.text}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Actionable Scorecard Section */}
            <section className="py-20 px-4 bg-gradient-to-b from-transparent to-white/5">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-magenta/20 to-purple-500/20 border border-magenta/20 flex items-center justify-center mx-auto mb-8">
                        <BarChart3 className="h-10 w-10 text-magenta" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">Actionable Scorecard</h2>
                    <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                        Get a clear picture of your website's health with our comprehensive scoring system.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-6 text-center">
                                <div className="text-4xl font-bold text-emerald-400 mb-2">A-F</div>
                                <p className="text-gray-400">Overall letter grade</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-6 text-center">
                                <div className="text-4xl font-bold text-cyan-400 mb-2">5+</div>
                                <p className="text-gray-400">Category breakdown</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-6 text-center">
                                <div className="text-4xl font-bold text-magenta mb-2">Pro</div>
                                <p className="text-gray-400">Easy-to-follow fixes</p>
                            </CardContent>
                        </Card>
                    </div>
                    <Button 
                        onClick={onRunAudit}
                        size="lg"
                        className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-10 py-6 text-lg"
                    >
                        Get Your Score Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <Footer onNavigate={onNavigate} />
        </div>
    );
}
